import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import qbdIvdQaradWhite from "@/assets/services/clinical-solutions/qbd-ivd-qarad-white.png";
import qbdIvdQaradLogo from "@/assets/services/clinical-solutions/qbd-ivd-qarad.png";
import { uiT } from "@/i18n/ui";
import { motion, AnimatePresence } from "framer-motion";
import SEOHead from "@/components/SEOHead";
import { ArrowRight, CheckCircle2, MessageCircle, Globe } from "lucide-react";
import Navbar from "@/components/Navbar";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import SplitBanner from "@/components/ui/SplitBanner";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { getSolutionBySlug, getSolutionByPath } from "@/data/solutionsData";
import type { SolutionData } from "@/data/solutionsData";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/hooks/useLanguage";
import { ES_SOLUTION_PATH_MAP, ZH_SOLUTION_PATH_MAP, getSolutionI18n } from "@/i18n/solutionDetail";
import { Section, SectionHeader, SectionDivider, AnimatedCard, FullBleedImage } from "@/components/solutions/shared";
import SolutionHero from "@/components/solutions/SolutionHero";
import SolutionSteps from "@/components/solutions/SolutionSteps";
import SolutionOfferings from "@/components/solutions/SolutionOfferings";
import SolutionBenefits from "@/components/solutions/SolutionBenefits";
import SolutionHelpCards from "@/components/solutions/SolutionHelpCards";
import SolutionTabbedServices from "@/components/solutions/SolutionTabbedServices";
import SolutionWhyQbd from "@/components/solutions/SolutionWhyQbd";
import SolutionRelatedInsights from "@/components/solutions/SolutionRelatedInsights";
import SolutionFAQ from "@/components/solutions/SolutionFAQ";
import SolutionFeaturedPodcast from "@/components/solutions/SolutionFeaturedPodcast";
import SolutionApproach from "@/components/solutions/SolutionApproach";
import SolutionClientCases from "@/components/solutions/SolutionClientCases";
import SolutionChallenges from "@/components/solutions/SolutionChallenges";
import type { LucideIcon } from "lucide-react";
import { FlaskConical, FileText, ShieldCheck, Microscope, Puzzle, Beaker, Layers, Sparkles, HelpCircle, BookOpen, Target } from "lucide-react";

const SolutionDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useStableLocation();
  const lang = useLanguage();
  const [formOpen, setFormOpen] = useState(false);
  const isMobile = useIsMobile();

  // Resolve ES path → EN path for data lookup
  const enPath = ES_SOLUTION_PATH_MAP[decodeURIComponent(location.pathname)] || ZH_SOLUTION_PATH_MAP[decodeURIComponent(location.pathname)] || location.pathname;
  const baseSolution = getSolutionByPath(enPath) ?? (slug ? getSolutionBySlug(slug) : undefined);

  // Overlay i18n translations onto base solution data
  const solution = useMemo<SolutionData | undefined>(() => {
    if (!baseSolution) return undefined;
    if (lang === "en") return baseSolution;
    const i18n = getSolutionI18n(baseSolution.slug, lang);
    if (!i18n) return baseSolution;
    return {
      ...baseSolution,
      title: i18n.title,
      subtitle: i18n.subtitle,
      description: i18n.description,
      ctaTitle: i18n.ctaTitle,
      ctaDescription: i18n.ctaDescription,
      ...(i18n.cardTitle && { cardTitle: i18n.cardTitle }),
      ...(i18n.challenges && { challenges: baseSolution.challenges.map((c, idx) => i18n.challenges![idx] ? { ...c, ...i18n.challenges![idx] } : c) }),
      ...(i18n.steps && { steps: baseSolution.steps.map((s, idx) => i18n.steps![idx] ? { ...s, ...i18n.steps![idx] } : s) }),
      ...(i18n.stepsTitle && { stepsTitle: i18n.stepsTitle }),
      ...(i18n.stepsSubtitle && { stepsSubtitle: i18n.stepsSubtitle }),
      ...(i18n.offerings && { offerings: baseSolution.offerings.map((o, idx) => i18n.offerings![idx] ? { ...o, ...i18n.offerings![idx] } : o) }),
      ...(i18n.benefits && { benefits: baseSolution.benefits.map((b, idx) => i18n.benefits![idx] ? { ...b, ...i18n.benefits![idx] } : b) }),
      ...(i18n.benefitsLabel && { benefitsLabel: i18n.benefitsLabel }),
      ...(i18n.faqs && { faqs: i18n.faqs }),
      ...(i18n.whyQbd && { whyQbd: baseSolution.whyQbd ? { ...baseSolution.whyQbd, ...i18n.whyQbd, points: i18n.whyQbd.points } : undefined }),
      ...(i18n.whyQbdSimple && baseSolution.whyQbdSimple && { whyQbdSimple: { ...baseSolution.whyQbdSimple, ...i18n.whyQbdSimple } }),
      ...(i18n.tailorMade && baseSolution.tailorMade && {
        tailorMade: {
          ...baseSolution.tailorMade,
          ...i18n.tailorMade,
          productTypes: baseSolution.tailorMade.productTypes.map((pt, idx) => i18n.tailorMade!.productTypes[idx] ? { ...pt, ...i18n.tailorMade!.productTypes[idx] } : pt),
        },
      }),
      ...(i18n.helpCards && baseSolution.helpCards && {
        helpCards: {
          ...baseSolution.helpCards,
          ...i18n.helpCards,
          cards: baseSolution.helpCards.cards.map((card, idx) => i18n.helpCards!.cards[idx] ? { ...card, ...i18n.helpCards!.cards[idx] } : card),
        },
      }),
      ...(i18n.marketSelector && baseSolution.marketSelector && {
        marketSelector: {
          ...baseSolution.marketSelector,
          ...i18n.marketSelector,
          markets: baseSolution.marketSelector.markets.map((m, idx) => i18n.marketSelector!.markets[idx] ? { ...m, ...i18n.marketSelector!.markets[idx] } : m),
        },
      }),
      ...(i18n.tabbedServices && baseSolution.tabbedServices && {
        tabbedServices: {
          ...baseSolution.tabbedServices,
          ...i18n.tabbedServices,
          tabs: baseSolution.tabbedServices.tabs.map((tab, idx) => i18n.tabbedServices!.tabs[idx] ? { ...tab, ...i18n.tabbedServices!.tabs[idx] } : tab),
        },
      }),
      ...(i18n.splitBanner && baseSolution.splitBanner && {
        splitBanner: { ...baseSolution.splitBanner, ...i18n.splitBanner },
      }),
      ...(i18n.solutionApproach && baseSolution.solutionApproach && {
        solutionApproach: { ...baseSolution.solutionApproach, ...i18n.solutionApproach },
      }),
      ...(i18n.challengesSection && baseSolution.challengesSection && {
        challengesSection: { ...baseSolution.challengesSection, ...i18n.challengesSection },
      }),
      ...(i18n.workshopCTA && baseSolution.workshopCTA && {
        workshopCTA: { ...baseSolution.workshopCTA, ...i18n.workshopCTA },
      }),
      ...(i18n.marketDifferentiators && baseSolution.marketDifferentiators && {
        marketDifferentiators: {
          ...baseSolution.marketDifferentiators,
          title: i18n.marketDifferentiators.title ?? baseSolution.marketDifferentiators.title,
          points: baseSolution.marketDifferentiators.points.map((p, idx) =>
            i18n.marketDifferentiators!.points[idx] ? { ...p, ...i18n.marketDifferentiators!.points[idx] } : p
          ),
        },
      }),
      ...(i18n.clientCasesTitle && { clientCasesTitle: i18n.clientCasesTitle }),
      ...(i18n.clientCasesSubtitle && { clientCasesSubtitle: i18n.clientCasesSubtitle }),
      ...(i18n.clientCases && baseSolution.clientCases && {
        clientCases: baseSolution.clientCases.map((c, idx) => i18n.clientCases![idx] ? { ...c, ...i18n.clientCases![idx] } : c),
      }),
      ...(i18n.sectionImages && baseSolution.sectionImages && {
        sectionImages: {
          ...baseSolution.sectionImages,
          ...(i18n.sectionImages.afterIntro && baseSolution.sectionImages.afterIntro && { afterIntro: { ...baseSolution.sectionImages.afterIntro, ...i18n.sectionImages.afterIntro } }),
          ...(i18n.sectionImages.beforeBenefits && baseSolution.sectionImages.beforeBenefits && { beforeBenefits: { ...baseSolution.sectionImages.beforeBenefits, ...i18n.sectionImages.beforeBenefits } }),
          ...(i18n.sectionImages.afterTimeline && baseSolution.sectionImages.afterTimeline && { afterTimeline: { ...baseSolution.sectionImages.afterTimeline, ...i18n.sectionImages.afterTimeline } }),
        },
      }),
      // Override path to use the Spanish one for canonical URLs
      path: location.pathname,
    };
  }, [baseSolution, lang, location.pathname]);

  if (!solution) return <Navigate to="/en/solutions" replace />;

  const t = uiT(lang);
  const categoryLabel = solution.category === "pharma" ? t("pharmaBiotech") : t("medDevicesIVD");
  const parentSolution = solution.parentSlug ? getSolutionBySlug(solution.parentSlug) : undefined;
  const SITE = "https://qbdgroup.com";

  return (
    <>
      <SEOHead
        title={`${solution.title} | QbD Group`}
        description={`${solution.subtitle ? solution.subtitle + ' ' : ''}${solution.description.split("\n\n")[0].replace(/\*\*/g, "")}`.slice(0, 160).replace(/\s+\S*$/, '…')}
        canonical={`${SITE}${solution.path}`}
        ogImage={solution.heroImage}
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": solution.title,
            "description": solution.description.replace(/\*\*/g, ""),
            "provider": {
              "@type": "Organization",
              "name": "QbD Group",
              "url": SITE,
              "logo": `${SITE}/assets/qbd-logo.webp`
            },
            "serviceType": categoryLabel,
            "areaServed": { "@type": "Place", "name": "Europe" },
            "url": `${SITE}${solution.path}`,
            "image": solution.heroImage,
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": ["h1", ".solution-description", ".solution-benefits"]
            },
            ...(solution.benefits.length > 0 && {
              "additionalProperty": solution.benefits.map(b => ({
                "@type": "PropertyValue",
                "name": b.title,
                "value": b.desc
              }))
            }),
            ...(solution.offerings.length > 0 && {
              "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": `${solution.title} — Offerings`,
                "itemListElement": solution.offerings.map((o, i) => ({
                  "@type": "Offer",
                  "position": i + 1,
                  "itemOffered": { "@type": "Service", "name": o.title, "description": o.items.join(", ") }
                }))
              }
            })
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": lang === "es" ? "Inicio" : lang === "zh-cn" ? "首页" : "Home", "item": `${SITE}/` },
              { "@type": "ListItem", "position": 2, "name": lang === "es" ? "Soluciones" : lang === "zh-cn" ? "解决方案" : "Solutions", "item": `${SITE}${lang === "es" ? "/es/soluciones" : lang === "zh-cn" ? "/zh-cn/solutions" : "/en/solutions"}` },
              ...(parentSolution ? [{ "@type": "ListItem", "position": 3, "name": parentSolution.title, "item": `${SITE}${parentSolution.path}` }] : []),
              { "@type": "ListItem", "position": parentSolution ? 4 : 3, "name": solution.title }
            ]
          })}
        </script>
        {/* HowTo schema from solution steps */}
        {solution.steps.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "name": solution.stepsTitle || `Our structured approach to ${solution.title}`,
              "description": solution.stepsSubtitle || solution.description.replace(/\*\*/g, "").slice(0, 200),
              "image": solution.heroImage,
              "step": solution.steps.map((s, i) => ({
                "@type": "HowToStep",
                "position": i + 1,
                "name": s.title,
                "text": s.desc.replace(/\*\*/g, ""),
              })),
            })}
          </script>
        )}
        {/* FAQ schema — use curated FAQs when available, fall back to auto-generated */}
        {(() => {
          // Curated FAQs are emitted by <SolutionFAQ> alongside the visible
          // section (including database-sourced overrides), so skip them here.
          if (solution.faqs && solution.faqs.length > 0) return null;
          const faqs: { q: string; a: string }[] = [];
          if (solution.challenges.length > 0) {
            faqs.push({
              q: `${t("faqChallengesQ")} ${solution.title.toLowerCase()}?`,
              a: solution.challenges.map(c => `${c.title}: ${c.desc}`).join(" "),
            });
          }
          if (solution.steps.length > 0) {
            faqs.push({
              q: `${t("faqApproachQ")} ${solution.title.toLowerCase()}?`,
              a: solution.steps.map((s, i) => `${i + 1}. ${s.title}: ${s.desc}`).join(" "),
            });
          }
          if (solution.benefits.length > 0) {
            faqs.push({
              q: `${t("faqBenefitsQ")} ${solution.title.toLowerCase()}?`,
              a: solution.benefits.map(b => `${b.title}: ${b.desc}`).join(" "),
            });
          }
          if (faqs.length === 0) return null;
          return (
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": faqs.map(f => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
              })}
            </script>
          );
        })()}
      </SEOHead>

      <ScrollToTop />
      <Navbar {...(solution.slug === "precision-dx" ? {
        logoOverride: {
          src: qbdIvdQaradWhite,
          srcDark: qbdIvdQaradLogo,
          alt: "QbD IVD | Qarad",
          className: "h-16 sm:h-[4.5rem] w-auto transition-opacity duration-300"
        }
      } : {})} />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={solution.accentColor ? { '--service-color': solution.accentColor } as React.CSSProperties : undefined}>
        <SolutionHero solution={solution} onCtaClick={() => setFormOpen(true)} />

        {solution.marketDifferentiators && (
          <Section bg className="py-16 sm:py-20">
            <div className="max-w-6xl mx-auto">
              <SectionHeader label={lang === "es" ? "Información de mercado" : lang === "zh-cn" ? "市场洞察" : "Market Insight"} title={solution.marketDifferentiators.title} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-10">
                {solution.marketDifferentiators.points.map((point, i) => {
                  const Icon = point.icon;
                  return (
                    <motion.div
                      key={i}
                      className="bg-card rounded-2xl p-6 sm:p-8 border border-border/60 shadow-sm hover:shadow-md transition-shadow h-full"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: i * 0.08 }}
                    >
                      {Icon && (
                        <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                          <Icon size={22} strokeWidth={1.75} />
                        </div>
                      )}
                      <h3 className="text-lg font-semibold text-foreground mb-2 leading-snug">{point.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{point.desc}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </Section>
        )}



        {/* Description — split layout with image for precision-dx */}
        {solution.sectionImages?.afterDescription && solution.slug === "precision-dx" ? (
          <Section className="py-16 sm:py-24">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 max-w-6xl mx-auto items-center">
              <div className="solution-description space-y-6">
                {solution.description.split("\n\n").map((p, i) => (
                  <p key={i} className={`${i === 0 ? 'text-lg sm:text-xl font-medium text-foreground/90 leading-[1.7]' : 'text-base text-muted-foreground leading-[1.85]'}`} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>') }} />
                ))}
                {solution.hubspotFormId && (
                  <div className="pt-6">
                    <Button variant="outline" className="rounded-full" onClick={() => setFormOpen(true)}>
                      {t("discussProject")} <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                )}
              </div>
              <motion.div
                className="rounded-2xl overflow-hidden shadow-xl shadow-primary/5"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img
                  src={solution.sectionImages.afterDescription.src}
                  alt={solution.sectionImages.afterDescription.alt}
                  className="w-full h-full object-cover rounded-2xl aspect-[4/3]"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </Section>
        ) : (
          <>
            <Section>
              <div className="max-w-3xl mx-auto space-y-6">
                {solution.description.split("\n\n").map((p, i) => (
                  <p key={i} className={`${i === 0 ? 'text-lg sm:text-xl font-medium text-foreground/90 leading-[1.7]' : 'text-base text-muted-foreground leading-[1.85]'}`} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>') }} />
                ))}
                {solution.hubspotFormId && !solution.hideDiscussCta && (
                  <div className="pt-6">
                    <Button variant="outline" className="rounded-full" onClick={() => setFormOpen(true)}>
                      {t("discussProject")} <ArrowRight size={14} className="ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            </Section>
            {solution.sectionImages?.afterDescription && (
              <section className="py-6 sm:py-10">
                <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
                  <motion.div
                    className="rounded-2xl overflow-hidden shadow-xl shadow-primary/5"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <img
                      src={solution.sectionImages.afterDescription.src}
                      alt={solution.sectionImages.afterDescription.alt}
                      className="w-full h-auto rounded-2xl"
                      loading="lazy"
                    />
                  </motion.div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Strategic Workshop CTA */}
        {solution.workshopCTA && (
          <Section bg>
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary">
                {solution.workshopCTA.eyebrow}
              </span>
              <h2 className="text-2xl sm:text-3xl font-semibold text-foreground leading-tight">
                {solution.workshopCTA.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                {solution.workshopCTA.body}
              </p>
              <div className="pt-2">
                <Button asChild variant="outline" className="rounded-full">
                  <a href={solution.workshopCTA.ctaHref} target="_blank" rel="noopener noreferrer">
                    {solution.workshopCTA.ctaLabel} <ArrowRight size={14} className="ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </Section>
        )}

        {/* Market Selector */}
        {solution.marketSelector && (
          <Section bg>
            <SectionHeader label={t("selectYourMarket")} title={solution.marketSelector.title} icon={Globe} />
            <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 leading-relaxed">
              {solution.marketSelector.subtitle}
            </p>
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {solution.marketSelector.markets.map((market, i) => {
                const isExternal = market.link.startsWith("http");
                const isHash = market.link.startsWith("#");
                const MotionLink = isExternal || isHash ? motion.a : motion(Link);
                const linkProps = isExternal
                  ? { href: market.link, target: "_blank", rel: "noopener noreferrer" }
                  : isHash
                  ? { href: market.link }
                  : { to: market.link };
                return (
                  <MotionLink
                    key={i}
                    {...(linkProps as any)}
                    className="group bg-card border border-border rounded-xl overflow-hidden hover:border-primary hover:shadow-lg hover:shadow-primary/5 transition-all duration-200"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -3 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.1 }}
                  >
                    {market.flagImage && (
                      <div className="aspect-[16/7] overflow-hidden relative">
                        <img src={market.flagImage} alt={`${market.label} flag`} className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />
                      </div>
                    )}
                    <div className="p-6">
                      <span className="text-sm font-semibold text-primary uppercase tracking-wide">{market.label}</span>
                      <h3 className="text-lg font-semibold text-foreground mt-1 mb-2 group-hover:text-primary transition-colors">{market.heading}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{market.desc}</p>
                      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                        {market.buttonLabel || t("learnMore")} <ArrowRight size={14} />
                      </span>
                    </div>
                  </MotionLink>
                );
              })}
            </div>
          </Section>
        )}

        <SectionDivider />

        <SolutionChallenges solution={solution} />

        <SolutionApproach solution={solution} onCtaClick={() => setFormOpen(true)} />

        <SolutionFeaturedPodcast solution={solution} />

        <SectionDivider />

        <SolutionSteps solution={solution} onCtaClick={() => setFormOpen(true)} />

        <SolutionOfferings solution={solution} onCtaClick={() => setFormOpen(true)} />

        {solution.splitBanner && !solution.sectionImages?.beforeBenefits ? (
          <SplitBanner
            title={solution.splitBanner.title}
            description={solution.splitBanner.description}
            image={solution.splitBanner.image}
            imageAlt={solution.splitBanner.imageAlt}
            ctaLabel={solution.splitBanner.ctaLabel}
            onCtaClick={() => setFormOpen(true)}
            accentColor={solution.accentColor}
            reversed={solution.splitBanner.reversed}
          />
        ) : solution.sectionImages?.beforeBenefits ? (
          <FullBleedImage image={solution.sectionImages.beforeBenefits} />
        ) : null}

        <SectionDivider />

        <SolutionBenefits solution={solution} onCtaClick={() => setFormOpen(true)} />

        {/* Tailor-made section */}
        {solution.tailorMade && (
          <Section>
            <SectionHeader label={lang === "zh-cn" ? "为您量身定制" : lang === "es" ? "Hecho a medida para ti" : "Tailored for you"} title={solution.tailorMade.title} icon={Sparkles} />
            <div className="max-w-3xl mx-auto space-y-4 mb-10">
              {solution.tailorMade.content.map((p, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
              ))}
              <p className="text-primary font-semibold text-center mt-6">{solution.tailorMade.ctaText}</p>
            </div>
            {solution.tailorMade.productTypes.length > 0 && (
              <div className="flex flex-wrap justify-center gap-8 max-w-4xl mx-auto">
                {solution.tailorMade.productTypes.map((pt, i) => {
                  const colors = [
                    "bg-primary/15 text-primary",
                    "bg-accent/15 text-accent",
                    "bg-emerald-500/15 text-emerald-600",
                    "bg-amber-500/15 text-amber-600",
                    "bg-rose-500/15 text-rose-600",
                    "bg-sky-500/15 text-sky-600",
                  ];
                  const colorClass = colors[i % colors.length];
                  const ProductIcon = pt.icon;
                  return (
                    <motion.div
                      key={i}
                      className="flex flex-col items-center gap-3 w-28"
                      initial={{ opacity: 0, y: 15 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                    >
                      <div className={`w-20 h-20 rounded-full ${colorClass} flex items-center justify-center`}>
                        {ProductIcon ? <ProductIcon size={32} strokeWidth={1.5} /> : pt.image ? <img src={pt.image} alt={pt.label} className="w-14 h-14 object-contain" loading="lazy" /> : null}
                      </div>
                      <span className="text-xs font-medium text-foreground text-center leading-tight">{pt.label}</span>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </Section>
        )}

        {/* Quote banner — show client quote for precision-dx, otherwise generic subtitle */}
        {solution.quoteBannerImage && solution.slug === "precision-dx" && solution.clientQuote ? (
          <section className="relative py-20 sm:py-28 overflow-hidden">
            <div className="absolute inset-0">
              <img src={solution.quoteBannerImage} alt="" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-foreground/50" />
            </div>
            <div className="container mx-auto px-6 lg:px-10 max-w-4xl relative z-10">
              <motion.div
                className="bg-background/90 backdrop-blur-md rounded-2xl px-8 sm:px-14 py-10 sm:py-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-5xl sm:text-6xl font-serif leading-none select-none" style={{ color: 'var(--service-color, var(--primary))', opacity: 0.25 }} aria-hidden>"</span>
                <blockquote className="text-lg sm:text-xl lg:text-2xl font-medium italic text-foreground leading-relaxed mb-8 -mt-4 max-w-3xl">
                  {solution.clientQuote.quote}
                </blockquote>
                <div className="flex items-center gap-4">
                  {solution.clientQuote.avatar && (
                    <img src={solution.clientQuote.avatar} alt={solution.clientQuote.name} className="w-12 h-12 rounded-full object-cover shrink-0" loading="lazy" />
                  )}
                  <div>
                    <p className="font-semibold text-foreground">{solution.clientQuote.name}</p>
                    {solution.clientQuote.title && <p className="text-sm text-muted-foreground">{solution.clientQuote.title}</p>}
                    <p className="text-sm" style={{ color: 'var(--service-color, var(--primary))' }}>{solution.clientQuote.company}</p>
                  </div>
                  {solution.clientQuote.logo && (
                    <img src={solution.clientQuote.logo} alt={solution.clientQuote.company} className="h-10 sm:h-12 w-auto object-contain ml-auto" loading="lazy" />
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        ) : solution.quoteBannerImage && (solution.whyQbdSimple?.subtitle || solution.whyQbdSimple?.title) ? (
          <section className="relative py-20 sm:py-28 overflow-hidden">
            <div className="absolute inset-0">
              <img src={solution.quoteBannerImage} alt="" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-foreground/40" />
            </div>
            <div className="container mx-auto px-6 lg:px-10 max-w-4xl relative z-10 flex items-center justify-center">
              <motion.blockquote
                className="bg-background/85 backdrop-blur-md rounded-2xl px-8 sm:px-12 py-8 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground leading-relaxed">
                  {solution.whyQbdSimple?.subtitle || solution.whyQbdSimple?.title}
                </p>
              </motion.blockquote>
            </div>
          </section>
        ) : null}

        {solution.slug === "precision-dx" ? (
          <>
            <SolutionTabbedServices solution={solution} />
            <SolutionWhyQbd solution={solution} onCtaClick={() => setFormOpen(true)} />
            <SolutionHelpCards solution={solution} onCtaClick={() => setFormOpen(true)} />
          </>
        ) : (
          <>
            <SolutionWhyQbd solution={solution} onCtaClick={() => setFormOpen(true)} />
            <SolutionHelpCards solution={solution} onCtaClick={() => setFormOpen(true)} />
            <SolutionTabbedServices solution={solution} />
          </>
        )}

        <SolutionClientCases solution={solution} onCtaClick={solution.hubspotFormId ? () => setFormOpen(true) : undefined} />

        {/* Client testimonial quote (skip for precision-dx — shown in quote banner above) */}
        {solution.clientQuote && solution.slug !== "precision-dx" && (
          <section className="py-16 sm:py-24 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
              <motion.div
                className="relative bg-card border border-border/60 rounded-2xl px-8 sm:px-14 py-10 sm:py-14"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {/* Large decorative quote mark */}
                <span
                  className="absolute top-6 left-8 sm:left-12 text-6xl sm:text-8xl font-serif leading-none select-none"
                  style={{ color: 'var(--service-color, var(--primary))', opacity: 0.2 }}
                  aria-hidden
                >
                  "
                </span>

                <div className="relative z-10 pt-8 sm:pt-6">
                  <blockquote className="text-lg sm:text-xl lg:text-2xl font-medium italic text-foreground leading-relaxed mb-8 max-w-3xl">
                    {solution.clientQuote.quote}
                  </blockquote>

                  <div className="flex items-center gap-4">
                    {solution.clientQuote.avatar ? (
                      <img src={solution.clientQuote.avatar} alt={solution.clientQuote.name} className="w-12 h-12 rounded-full object-cover shrink-0" loading="lazy" />
                    ) : (
                      <span className="w-10 h-0.5 rounded-full shrink-0" style={{ backgroundColor: 'var(--service-color, var(--primary))' }} />
                    )}
                    <div>
                      <p className="font-semibold text-foreground">{solution.clientQuote.name}</p>
                      {solution.clientQuote.title && (
                        <p className="text-sm text-muted-foreground">{solution.clientQuote.title}</p>
                      )}
                      <p className="text-sm text-primary">{solution.clientQuote.company}</p>
                    </div>
                    {solution.clientQuote.logo && (
                      <img
                        src={solution.clientQuote.logo}
                        alt={solution.clientQuote.company}
                        className="h-10 sm:h-12 w-auto object-contain ml-auto"
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {solution.splitBanner && solution.sectionImages?.beforeBenefits && (
          <SplitBanner
            title={solution.splitBanner.title}
            description={solution.splitBanner.description}
            image={solution.splitBanner.image}
            imageAlt={solution.splitBanner.imageAlt}
            ctaLabel={solution.splitBanner.ctaLabel}
            onCtaClick={() => setFormOpen(true)}
            accentColor={solution.accentColor}
            reversed={solution.splitBanner.reversed}
          />
        )}

        {!solution.hideFaq && <SolutionFAQ solution={solution} />}


        {/* Inquiry Form Dialog */}
        {solution.hubspotFormId && (
          <Dialog open={formOpen} onOpenChange={setFormOpen}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogTitle className="sr-only">{solution.ctaTitle || "Contact us"}</DialogTitle>
              <WebinarRegistrationForm
                registrationUrl={null}
                title={solution.title}
                hubspotFormId={solution.hubspotFormId}
                heading={solution.ctaTitle}
                subtitle={solution.ctaDescription}
              />
            </DialogContent>
          </Dialog>
        )}

        <SolutionRelatedInsights solution={solution} />

        <ContactCTA
          onContactClick={solution.hubspotFormId ? () => setFormOpen(true) : undefined}
          title={solution.ctaTitle}
          description={solution.ctaDescription}
        />
      </main>

      {/* Sticky mobile CTA */}
      {solution.hubspotFormId && (
        <AnimatePresence>
          {isMobile && (
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              className="fixed bottom-0 inset-x-0 z-50 sm:hidden bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3 flex items-center gap-3"
            >
              <Button className="flex-1 rounded-full" onClick={() => setFormOpen(true)}>
                <MessageCircle size={16} className="mr-2" />
                {t("talkToExpert")}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      <Footer />
    </>
  );
};

export default SolutionDetail;
